import { useEffect, useMemo, useState } from "react";
import { Drawer, message, Modal } from "antd";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import { careService } from "../../services/careService";
import { addressFieldsFromPlaceDetails } from "../../utils/googlePlaces";

const pageItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const defaultPharmacyForm = () => ({
  name: "",
  phone: "+1 ",
  fax: "+1 ",
  email: "",
  website: "",
  addressSearch: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  contactName: "",
  status: "ACTIVE",
  notes: "",
});

const pharmacyFormFromRecord = (pharmacy = {}) => ({
  ...defaultPharmacyForm(),
  name: pharmacy.name || "",
  phone: pharmacy.phone || "+1 ",
  fax: pharmacy.fax || "+1 ",
  email: pharmacy.email || "",
  website: pharmacy.website || "",
  addressSearch: pharmacyAddress(pharmacy) || pharmacy.addressLine1 || "",
  addressLine1: pharmacy.addressLine1 || "",
  addressLine2: pharmacy.addressLine2 || "",
  city: pharmacy.city || "",
  state: pharmacy.state || "",
  postalCode: pharmacy.postalCode || "",
  country: pharmacy.country || "",
  contactName: pharmacy.contactName || "",
  status: pharmacy.status || "ACTIVE",
  notes: pharmacy.notes || "",
});

const pharmacyAddress = (pharmacy = {}) =>
  [pharmacy.addressLine1, pharmacy.addressLine2, pharmacy.city, pharmacy.state, pharmacy.postalCode, pharmacy.country]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(", ");

const websiteHref = (value) => {
  const website = String(value || "").trim();
  if (!website) return "";
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
};

export default function PharmaciesPage() {
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(() => defaultPharmacyForm());
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const workspaceParams = useMemo(
    () => ({ wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId }),
    [selectedWorkspaceId],
  );

  const loadPharmacies = () => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setPharmacies([]);
      return;
    }
    setLoading(true);
    setError("");
    careService.pharmacies.list(workspaceParams)
      .then((data) => setPharmacies(pageItems(data)))
      .catch((err) => {
        setPharmacies([]);
        setError(err?.response?.data?.message || err?.message || "Unable to load pharmacies.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPharmacies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspaceId, workspaceListReady]);

  const filteredPharmacies = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return pharmacies;
    return pharmacies.filter((pharmacy) => [
      pharmacy.name,
      pharmacy.phone,
      pharmacy.email,
      pharmacyAddress(pharmacy),
    ].join(" ").toLowerCase().includes(term));
  }, [pharmacies, search]);

  const openNew = () => {
    setEditingId("");
    setForm(defaultPharmacyForm());
    setDrawerOpen(true);
  };

  const openEdit = (pharmacy) => {
    setDetailsOpen(false);
    setEditingId(pharmacy?.id || "");
    setForm(pharmacyFormFromRecord(pharmacy));
    setDrawerOpen(true);
  };

  const openDetails = (pharmacy) => {
    setSelectedPharmacy(pharmacy);
    setDetailsOpen(true);
  };

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const applyAddressFields = (fields = {}) => {
    setForm((current) => ({
      ...current,
      addressSearch: fields.search ?? current.addressSearch,
      addressLine1: fields.addressLine1 ?? current.addressLine1,
      city: fields.city ?? current.city,
      state: fields.state ?? current.state,
      postalCode: fields.postalCode ?? fields.zipCode ?? current.postalCode,
      country: fields.country ?? current.country,
    }));
  };

  const copyContactValue = async (value, label) => {
    const text = String(value || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${label} copied.`);
    } catch {
      message.error(`Unable to copy ${label.toLowerCase()}.`);
    }
  };

  const savePharmacy = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      message.error("Pharmacy name is required.");
      return;
    }
    setSaving(true);
    try {
      const { addressSearch, ...formPayload } = form;
      const payload = {
        ...formPayload,
        name: form.name.trim(),
        phone: /^\+1\s*$/.test(form.phone) ? "" : form.phone.trim(),
        fax: /^\+1\s*$/.test(form.fax) ? "" : form.fax.trim(),
        workspaceId: selectedWorkspaceId,
      };
      const saved = editingId
        ? await careService.pharmacies.update(editingId, payload, workspaceParams)
        : await careService.pharmacies.create(payload, workspaceParams);
      setPharmacies((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      setSelectedPharmacy((current) => current?.id === saved.id ? saved : current);
      setDrawerOpen(false);
      setEditingId("");
      message.success("Pharmacy saved.");
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to save pharmacy.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDeletePharmacy = (pharmacy) => {
    const pharmacyId = pharmacy?.id || pharmacy?.pharmacyId;
    if (!pharmacyId || deletingId) return;
    Modal.confirm({
      title: "Delete Pharmacy?",
      content: <p>Delete <strong>{firstText(pharmacy.name, "this pharmacy")}</strong> from the pharmacy directory? This action cannot be undone.</p>,
      okText: "Delete Pharmacy",
      cancelText: "Cancel",
      className: "pharmacy-delete-confirm-modal",
      okButtonProps: { danger: true, className: "pharmacy-delete-confirm-button" },
      cancelButtonProps: { className: "pharmacy-delete-confirm-cancel" },
      onOk: async () => {
        setDeletingId(pharmacyId);
        try {
          await careService.pharmacies.delete(pharmacyId, workspaceParams);
          setPharmacies((current) => current.filter((item) => (item.id || item.pharmacyId) !== pharmacyId));
          if ((selectedPharmacy?.id || selectedPharmacy?.pharmacyId) === pharmacyId) {
            setDetailsOpen(false);
            setSelectedPharmacy(null);
          }
          message.success("Pharmacy deleted.");
        } catch (err) {
          message.error(err?.response?.data?.message || err?.message || "Unable to delete pharmacy.");
          throw err;
        } finally {
          setDeletingId("");
        }
      },
    });
  };

  return (
    <CareShell className="dashboard-content pharmacies-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Pharmacies</h1>
          <p>Manage pharmacies used to receive and coordinate resident medications.</p>
        </div>
        <button className="mini-btn pharmacy-primary-button" type="button" onClick={openNew}>
          <i className="ti ti-plus" /> Add Pharmacy
        </button>
      </div>

      <Panel title="Pharmacy Directory">
        {error ? <p className="form-error">{error}</p> : null}
        <label className="resident-search medication-resident-search">
          <i className="ti ti-search" />
          <input placeholder="Search pharmacies" value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        <div className="medication-table-wrap">
          <table className="medication-table">
            <thead>
              <tr>
                <th>Pharmacy</th>
                <th>Contact</th>
                <th>Address</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={5}>Loading pharmacies...</td></tr> : null}
              {!loading && !filteredPharmacies.length ? <tr><td colSpan={5}>No pharmacies found.</td></tr> : null}
              {!loading && filteredPharmacies.map((pharmacy) => (
                <tr key={pharmacy.id || pharmacy.name}>
                  <td>
                    <strong>{firstText(pharmacy.name, "Unnamed pharmacy")}</strong>
                    <small>{firstText(pharmacy.website, pharmacy.email, "")}</small>
                  </td>
                  <td>
                    {firstText(pharmacy.contactName, "Not added")}
                    <small className="pharmacy-contact-line">
                      <span>{pharmacy.fax ? `Fax: ${pharmacy.fax}` : "Fax: Not added"}</span>
                      {pharmacy.fax ? <button type="button" onClick={() => copyContactValue(pharmacy.fax, "Fax number")} aria-label={`Copy ${pharmacy.name} fax number`} title="Copy fax number"><i className="ti ti-copy" /></button> : null}
                    </small>
                    <small className="pharmacy-contact-line">
                      <span>{pharmacy.phone ? `Phone: ${pharmacy.phone}` : "Phone: Not added"}</span>
                      {pharmacy.phone ? <button type="button" onClick={() => copyContactValue(pharmacy.phone, "Phone number")} aria-label={`Copy ${pharmacy.name} phone number`} title="Copy phone number"><i className="ti ti-copy" /></button> : null}
                    </small>
                    {pharmacy.email ? (
                      <small className="pharmacy-contact-line">
                        <span>{pharmacy.email}</span>
                        <button type="button" onClick={() => copyContactValue(pharmacy.email, "Email address")} aria-label={`Copy ${pharmacy.name} email address`} title="Copy email address"><i className="ti ti-copy" /></button>
                      </small>
                    ) : null}
                  </td>
                  <td>{firstText(pharmacyAddress(pharmacy), "Not added")}</td>
                  <td><em className="pill low">{firstText(pharmacy.status, "ACTIVE")}</em></td>
                  <td>
                    <div className="medication-row-actions">
                      <button className="resident-icon-button pharmacy-view-button" type="button" aria-label={`View ${pharmacy.name} details`} onClick={() => openDetails(pharmacy)}>
                        <i className="ti ti-eye" />
                      </button>
                      <button className="resident-icon-button pharmacy-edit-button" type="button" aria-label={`Edit ${pharmacy.name}`} onClick={() => openEdit(pharmacy)}>
                        <i className="ti ti-edit" />
                      </button>
                      <button className="resident-icon-button pharmacy-delete-button" type="button" aria-label={`Delete ${pharmacy.name}`} title="Delete pharmacy" onClick={() => confirmDeletePharmacy(pharmacy)} disabled={deletingId === (pharmacy.id || pharmacy.pharmacyId)}>
                        <i className={`ti ${deletingId === (pharmacy.id || pharmacy.pharmacyId) ? "ti-loader-2" : "ti-trash"}`} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Drawer
        className="assessment-drawer pharmacy-details-drawer"
        destroyOnClose
        onClose={() => setDetailsOpen(false)}
        open={detailsOpen}
        title="Pharmacy Details"
        width="min(760px, 96vw)"
      >
        {selectedPharmacy ? (
          <div className="pharmacy-details-content">
            <section className="pharmacy-details-hero">
              <span className="pharmacy-details-icon"><i className="ti ti-building-store" /></span>
              <div>
                <span>Pharmacy</span>
                <h2>{firstText(selectedPharmacy.name, "Unnamed pharmacy")}</h2>
                <em className="pill low">{firstText(selectedPharmacy.status, "ACTIVE")}</em>
              </div>
            </section>

            <section className="assessment-drawer-section">
              <h3>Contact Information</h3>
              <dl className="pharmacy-details-grid">
                <div><dt>Contact name</dt><dd>{firstText(selectedPharmacy.contactName, "Not added")}</dd></div>
                <div><dt>Fax</dt><dd className="pharmacy-detail-copy-value">{selectedPharmacy.fax ? <><span>{selectedPharmacy.fax}</span><button type="button" onClick={() => copyContactValue(selectedPharmacy.fax, "Fax number")} aria-label="Copy fax number" title="Copy fax number"><i className="ti ti-copy" /></button></> : "Not added"}</dd></div>
                <div><dt>Phone</dt><dd className="pharmacy-detail-copy-value">{selectedPharmacy.phone ? <><a href={`tel:${selectedPharmacy.phone}`}>{selectedPharmacy.phone}</a><button type="button" onClick={() => copyContactValue(selectedPharmacy.phone, "Phone number")} aria-label="Copy phone number" title="Copy phone number"><i className="ti ti-copy" /></button></> : "Not added"}</dd></div>
                <div><dt>Email</dt><dd className="pharmacy-detail-copy-value">{selectedPharmacy.email ? <><a href={`mailto:${selectedPharmacy.email}`}>{selectedPharmacy.email}</a><button type="button" onClick={() => copyContactValue(selectedPharmacy.email, "Email address")} aria-label="Copy email address" title="Copy email address"><i className="ti ti-copy" /></button></> : "Not added"}</dd></div>
                <div className="pharmacy-details-wide"><dt>Website</dt><dd>{selectedPharmacy.website ? <a href={websiteHref(selectedPharmacy.website)} target="_blank" rel="noreferrer">{selectedPharmacy.website}</a> : "Not added"}</dd></div>
              </dl>
            </section>

            <section className="assessment-drawer-section">
              <h3>Address</h3>
              <p className="pharmacy-details-address">{firstText(pharmacyAddress(selectedPharmacy), "No address added")}</p>
            </section>

            <section className="assessment-drawer-section">
              <h3>Notes</h3>
              <p className="pharmacy-details-notes">{firstText(selectedPharmacy.notes, "No notes added")}</p>
            </section>

            <div className="drawer-actions">
              <button className="mini-btn pharmacy-drawer-cancel" type="button" onClick={() => setDetailsOpen(false)}>Close</button>
              <button className="mini-btn pharmacy-primary-button" type="button" onClick={() => openEdit(selectedPharmacy)}><i className="ti ti-edit" /> Edit Pharmacy</button>
            </div>
          </div>
        ) : null}
      </Drawer>

      <Drawer
        className="assessment-drawer medication-add-drawer"
        destroyOnClose
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        title={editingId ? "Edit Pharmacy" : "Add Pharmacy"}
        width="min(980px, 96vw)"
      >
        <form className="assessment-drawer-form" onSubmit={savePharmacy}>
          <div className="pharmacy-form-columns">
          <section className="assessment-drawer-section pharmacy-form-column">
            <h3>Pharmacy Information</h3>
            <div className="assessment-drawer-grid medication-add-grid">
              <label className="ncp-form-field ncp-form-field-wide"><span>Name</span><input value={form.name} onChange={(event) => updateForm("name", event.target.value)} required /></label>
              <label className="ncp-form-field"><span>Fax</span><input type="tel" value={form.fax} onChange={(event) => updateForm("fax", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Phone</span><input type="tel" value={form.phone} onChange={(event) => updateForm("phone", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Email</span><input type="email" value={form.email} onChange={(event) => updateForm("email", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Website</span><input value={form.website} onChange={(event) => updateForm("website", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Contact name</span><input value={form.contactName} onChange={(event) => updateForm("contactName", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Status</span><select value={form.status} onChange={(event) => updateForm("status", event.target.value)}><option value="ACTIVE">Active</option><option value="INACTIVE">Inactive</option></select></label>
            </div>
          </section>
          <section className="assessment-drawer-section pharmacy-form-column">
            <h3>Address &amp; Notes</h3>
            <label className="ncp-form-field ncp-form-field-wide pharmacy-address-search">
              <span>Address search</span>
              {appConfigLoading && !googleMapsApiKey ? (
                <input type="text" disabled placeholder="Loading map address search..." />
              ) : (
                <GooglePlacesAutocompleteInput
                  googleMapsApiKey={googleMapsApiKey}
                  placeholder={googleMapsApiKey ? "Search pharmacy address" : "Enter pharmacy address"}
                  popupClassName="pharmacy-address-search-dropdown"
                  types={["address"]}
                  value={form.addressSearch}
                  onChange={(nextValue) => {
                    if (!nextValue) {
                      applyAddressFields({
                        search: "",
                        addressLine1: "",
                        city: "",
                        state: "",
                        postalCode: "",
                        country: "",
                      });
                      return;
                    }
                    updateForm("addressSearch", nextValue);
                    if (!googleMapsApiKey) updateForm("addressLine1", nextValue);
                  }}
                  onResolvePlace={async ({ details, prediction }) => {
                    const resolvedAddress = details?.formatted_address || prediction?.description || form.addressSearch || "";
                    const fields = details ? addressFieldsFromPlaceDetails(details) : {};
                    applyAddressFields({
                      ...fields,
                      search: resolvedAddress,
                      addressLine1: fields.addressLine1 || resolvedAddress,
                      postalCode: fields.postalCode || fields.zipCode || "",
                    });
                  }}
                />
              )}
            </label>
            <div className="assessment-drawer-grid medication-add-grid">
              <label className="ncp-form-field"><span>Address line 1</span><input value={form.addressLine1} onChange={(event) => updateForm("addressLine1", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Address line 2</span><input value={form.addressLine2} onChange={(event) => updateForm("addressLine2", event.target.value)} /></label>
              <label className="ncp-form-field"><span>City</span><input value={form.city} onChange={(event) => updateForm("city", event.target.value)} /></label>
              <label className="ncp-form-field"><span>State</span><input value={form.state} onChange={(event) => updateForm("state", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Postal code</span><input value={form.postalCode} onChange={(event) => updateForm("postalCode", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Country</span><input value={form.country} onChange={(event) => updateForm("country", event.target.value)} /></label>
            </div>
            <label className="ncp-form-field ncp-form-field-wide pharmacy-notes-field"><span>Notes</span><textarea rows={3} value={form.notes} onChange={(event) => updateForm("notes", event.target.value)} /></label>
          </section>
          </div>
          <div className="drawer-actions">
            <button className="mini-btn pharmacy-drawer-cancel" type="button" onClick={() => setDrawerOpen(false)}>Cancel</button>
            <button className="mini-btn pharmacy-primary-button" type="submit" disabled={saving}>{saving ? "Saving..." : "Save Pharmacy"}</button>
          </div>
        </form>
      </Drawer>
    </CareShell>
  );
}
